import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, Space, Typography, message } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, LogoutOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from './AuthContext';
import theme from './theme';

const { Text } = Typography;

// Generic error messages to prevent account enumeration
const handleAuthError = (error) => {
  const genericMessages = {
    'auth/user-not-found': 'Invalid email or password',
    'auth/wrong-password': 'Invalid email or password',
    'auth/invalid-email': 'Invalid email or password',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/invalid-credential': 'Invalid email or password',
    'default': 'Authentication failed. Please try again.'
  };

  return genericMessages[error.code] || genericMessages.default;
};

// Rate limiting hook
const useRateLimit = (maxAttempts = 5, windowMs = 30 * 1000) => {
  const [attempts, setAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState(null);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('authAttempts');
    if (stored) {
      const data = JSON.parse(stored);
      const now = Date.now();

      if (data.lockoutTime && now < data.lockoutTime) {
        setLockoutTime(data.lockoutTime);
        setAttempts(data.attempts);
      } else if (data.lockoutTime && now >= data.lockoutTime) {
        // Lockout expired, reset
        localStorage.removeItem('authAttempts');
        setAttempts(0);
        setLockoutTime(null);
      } else {
        setAttempts(data.attempts || 0);
      }
    }
  }, []);

  // Timer to automatically clear lockout when it expires
  useEffect(() => {
    if (!lockoutTime) return;

    const now = Date.now();
    const timeRemaining = lockoutTime - now;

    if (timeRemaining <= 0) {
      // Already expired, clear immediately
      setAttempts(0);
      setLockoutTime(null);
      localStorage.removeItem('authAttempts');
      return;
    }

    // Set timer to clear lockout when it expires
    const timer = setTimeout(() => {
      setAttempts(0);
      setLockoutTime(null);
      localStorage.removeItem('authAttempts');
    }, timeRemaining);

    return () => clearTimeout(timer);
  }, [lockoutTime]);

  const checkRateLimit = useCallback(() => {
    const now = Date.now();

    if (lockoutTime && now < lockoutTime) {
      const remainingSeconds = Math.ceil((lockoutTime - now) / 1000);
      throw new Error(`Too many login attempts. Please try again in ${remainingSeconds} second${remainingSeconds > 1 ? 's' : ''}.`);
    }

    if (lockoutTime && now >= lockoutTime) {
      // Lockout expired, reset
      setAttempts(0);
      setLockoutTime(null);
      localStorage.removeItem('authAttempts');
      return true;
    }

    if (attempts >= maxAttempts) {
      const newLockoutTime = now + windowMs;
      setLockoutTime(newLockoutTime);
      localStorage.setItem('authAttempts', JSON.stringify({
        attempts: attempts,
        lockoutTime: newLockoutTime
      }));
      const remainingSeconds = Math.ceil(windowMs / 1000);
      throw new Error(`Too many login attempts. Please try again in ${remainingSeconds} seconds.`);
    }

    return true;
  }, [attempts, lockoutTime, maxAttempts, windowMs]);

  const recordAttempt = useCallback(() => {
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    localStorage.setItem('authAttempts', JSON.stringify({
      attempts: newAttempts,
      lockoutTime: null
    }));
  }, [attempts]);

  const resetAttempts = useCallback(() => {
    setAttempts(0);
    setLockoutTime(null);
    localStorage.removeItem('authAttempts');
  }, []);

  return { checkRateLimit, recordAttempt, resetAttempts, attempts, isLockedOut: lockoutTime && Date.now() < lockoutTime };
};

const LoginComponent = () => {
  const { user, loginWithEmail, logout, sendLoginLink, emailLinkSignInRequired, completeSignInWithLink } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const { checkRateLimit, recordAttempt, resetAttempts, isLockedOut } = useRateLimit();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      message.warning('Please enter both email and password');
      return;
    }

    try {
      checkRateLimit();
    } catch (error) {
      message.error(error.message);
      return;
    }

    setLoading(true);
    try {
      await loginWithEmail(email, password);
      message.success('Welcome back!');
      resetAttempts();
      setEmail('');
      setPassword('');
    } catch (error) {
      recordAttempt();
      const errorMessage = handleAuthError(error);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendLink = async (e) => {
    e.preventDefault();
    if (!email) {
      message.warning('Please enter your email');
      return;
    }

    try {
      checkRateLimit();
    } catch (error) {
      message.error(error.message);
      return;
    }

    setLoading(true);
    try {
      await sendLoginLink(email);
      setLinkSent(true);
      message.success('Login link sent! Check your email.');
    } catch (error) {
      recordAttempt();
      message.error('Failed to send login link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSignIn = async (e) => {
    e.preventDefault();
    if (!email) {
      message.warning('Please enter your email to complete sign-in');
      return;
    }

    setLoading(true);
    try {
      await completeSignInWithLink(email);
      message.success('Welcome back!');
      setEmail('');
    } catch (error) {
      message.error('Failed to complete sign-in. Please try again or request a new link.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      message.info('Logged out successfully');
    } catch (error) {
      message.error('Logout failed: ' + error.message);
    }
  };

  // Prompt for email when the link is opened in a different browser
  if (emailLinkSignInRequired) {
    return (
      <div className="auth-section">
        <form onSubmit={handleCompleteSignIn}>
          <Space size="small" wrap>
            <Text style={{ color: theme.colors.textWhite }}>Confirm your email to sign in:</Text>
            <Input
              prefix={<MailOutlined />}
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '200px' }}
              required
            />
            <Button type="primary" htmlType="submit" loading={loading} icon={<LoginOutlined />}>
              Complete Sign-In
            </Button>
          </Space>
        </form>
      </div>
    );
  }

  if (user) {
    return (
      <div className="auth-section">
        <Space size="middle">
          <Text style={{ color: theme.colors.textWhite, fontWeight: 500 }}>
            {user.email}
          </Text>
          <Button
            type="default"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              backgroundColor: theme.colors.warning,
              borderColor: theme.colors.warning,
              color: theme.colors.textWhite
            }}
          >
            Logout
          </Button>
        </Space>
      </div>
    );
  }

  if (linkSent) {
    return (
      <div className="auth-section">
        <Space size="middle">
          <MailOutlined style={{ color: theme.colors.textWhite, fontSize: '16px' }} />
          <Text style={{ color: theme.colors.textWhite }}>
            Link sent to {email}. Check your inbox!
          </Text>
          <Button
            size="small"
            onClick={() => { setLinkSent(false); setEmail(''); }}
          >
            Back
          </Button>
        </Space>
      </div>
    );
  }

  if (linkMode) {
    return (
      <div className="auth-section">
        <form onSubmit={handleSendLink}>
          <Space size="small" wrap>
            <Input
              prefix={<MailOutlined />}
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '200px' }}
              required
              disabled={isLockedOut}
            />
            <Button
              type="primary"
              htmlType="submit"
              icon={<MailOutlined />}
              loading={loading}
              disabled={isLockedOut}
            >
              Send Login Link
            </Button>
            <Button
              size="small"
              onClick={() => { setLinkMode(false); setEmail(''); }}
              disabled={loading}
            >
              Use Password
            </Button>
          </Space>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-section">
      <form onSubmit={handleLogin}>
        <Space size="small" wrap>
          <Input
            prefix={<UserOutlined />}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: '180px' }}
            required
            disabled={isLockedOut}
          />
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ width: '180px' }}
            required
            disabled={isLockedOut}
          />
          <Button
            type="primary"
            htmlType="submit"
            icon={<LoginOutlined />}
            loading={loading}
            disabled={isLockedOut}
          >
            Login
          </Button>
          <Button
            size="small"
            onClick={() => { setLinkMode(true); setPassword(''); }}
            disabled={isLockedOut || loading}
          >
            Login Link
          </Button>
        </Space>
      </form>
    </div>
  );
};

export default LoginComponent;